import { useTranslation } from 'react-i18next';
import { ButtonLink, Container, Heading, PageHeader, Text } from '@bn-apps/dim-ui-react';
import routes from '@config/routes';
import appStyles from '@/App.module.scss';
import styles from './AccessDenied.module.scss';

interface AccessDeniedProps {
    reason?: string;
    requiredLevel?: string;
}

/**
 * AccessDenied Component
 *
 * Displays when a user attempts to access a resource they don't have permission for.
 * Provides clear feedback and guidance on how to proceed.
 *
 * Accessibility Features:
 * - ARIA role="alert" for immediate screen reader announcement
 * - aria-describedby linking to detailed explanation
 * - Clear, descriptive error messages
 * - Keyboard-accessible actions
 */
const AccessDenied = ({ reason, requiredLevel = 'Project Member' }: AccessDeniedProps): JSX.Element => {
  const { t } = useTranslation();

  return (
      <Container className={appStyles.pageContent}>
        {/* Hero Section */}
        <div className="bn-mbe-2000">
          <PageHeader
            className={styles.header}
            title={`🔒 ${t('auth.accessDenied.title')}`}
            lead={reason || t('auth.accessDenied.message', { requiredLevel })}
          />

          <div className={styles.explanation}>
            <Heading level={2} className={styles.subheading}>
              {t('auth.accessDenied.whyTitle')}
            </Heading>
            <Text className={styles.paragraph}>
              {t('auth.accessDenied.whyMessage', { requiredLevel })}
            </Text>

            <Heading level={2} className={styles.subheading}>
              {t('auth.accessDenied.whatToDoTitle')}
            </Heading>
            <ul>
              <li className={styles.item}>
                <Text>{t('auth.accessDenied.contactAdmin')}</Text>
              </li>
              <li className={styles.item}>
                <Text>{t('auth.accessDenied.verifyRole')}</Text>
              </li>
              <li className={styles.item}>
                <Text>{t('auth.accessDenied.tryLogin')}</Text>
              </li>
              <li className={styles.item}>
                <Text>
                  {t('help.contactPrompt')}{' '}
                  <a href={`mailto:${t('footer.supportEmail')}`}>{t('help.contactAction')}</a>
                </Text>
              </li>
            </ul>
          </div>

          <div className={styles.actions}>
            <ButtonLink href={routes.home} variant="primary">
              {t('common.goHome')}
            </ButtonLink>
            <ButtonLink href={routes.help} variant="secondary">
              {t('common.helpCenter')}
            </ButtonLink>
          </div>
      </div>
    </Container>
  );
};

export default AccessDenied;
